import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import HeaderWrapper from "./header.style";
import { UncontrolledPopover, PopoverBody } from "reactstrap";
import { ProfileLockScreen } from "helper/constant";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router-dom";
import { injectIntl } from "react-intl";
import moment from "moment";
import Api from "api/Api";
import Toaster from "components/common/Toaster";
import { filterMenuCatalog, getMenuCatalog } from "util/menuSearch";
import { logoutAndClearSession } from "redux/auth/logoutAndClearSession";

const SEARCH_DEBOUNCE_MS = 280;

const Header = props => {
  const { drawerMiniMethod, intl, history, isStore, storeId } = props;
  const toaster = useRef();
  const searchWrapRef = useRef(null);
  const searchInputRef = useRef(null);
  const bellWrapRef = useRef(null);
  const debounceRef = useRef(null);
  const requestSeq = useRef(0);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [entityResults, setEntityResults] = useState({
    stores: [],
    devices: [],
    receipts: []
  });

  const adminType = props.authData && props.authData.adminType;
  const displayName =
    adminType === "MASTER"
      ? "Admin User"
      : (props.authData && props.authData.storeName) || "Admin User";
  const displayRole = adminType
    ? adminType.charAt(0) + adminType.slice(1).toLowerCase() + " Admin"
    : "Admin";

  const menuCatalog = useMemo(
    () =>
      getMenuCatalog({
        isStore: !!isStore,
        adminType,
        messages: (intl && intl.messages) || {}
      }),
    [isStore, adminType, intl]
  );

  const menuResults = useMemo(() => filterMenuCatalog(menuCatalog, query), [
    menuCatalog,
    query
  ]);

  const combinedResults = useMemo(() => {
    const rows = [];
    menuResults.forEach(item => {
      rows.push({
        ...item,
        subtitle: item.group
      });
    });
    (entityResults.stores || []).forEach(store => {
      rows.push({
        type: "store",
        id: `store:${store.storeId}`,
        label: store.storeName || `Store #${store.storeId}`,
        subtitle: "Store",
        icon: "fa fa-store",
        group: "Stores",
        data: store
      });
    });
    (entityResults.devices || []).forEach(device => {
      rows.push({
        type: "device",
        id: `device:${device.deviceTokenId}`,
        label:
          device.deviceEmail ||
          device.deviceName ||
          `Device #${device.deviceTokenId}`,
        subtitle: device.storeName ? `Device · ${device.storeName}` : "Device",
        icon: "fas fa-mobile-alt",
        group: "Devices",
        data: device
      });
    });
    (entityResults.receipts || []).forEach(receipt => {
      rows.push({
        type: "receipt",
        id: `receipt:${receipt.receiptId || receipt.id}`,
        label:
          receipt.receiptNumber ||
          receipt.invoiceNumber ||
          `Receipt #${receipt.receiptId || receipt.id}`,
        subtitle: receipt.storeName
          ? `Receipt · ${receipt.storeName}`
          : "Receipt / invoice",
        icon: "fas fa-receipt",
        group: "Receipts",
        data: receipt
      });
    });
    return rows;
  }, [menuResults, entityResults]);

  useEffect(() => {
    const onDocMouseDown = e => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setOpen(false);
      }
      if (bellWrapRef.current && !bellWrapRef.current.contains(e.target)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const loadNotifications = useCallback(() => {
    setNotificationsLoading(true);
    Api.post("/admin/notification/list", {
      page: { limit: 8, pageId: 1 },
      sortBy: { direction: "DESC", property: "createdAt" },
      search: ""
    })
      .then(r => {
        setNotifications(r.data?.data?.list || []);
      })
      .catch(() => {
        setNotifications([]);
      })
      .finally(() => {
        setNotificationsLoading(false);
      });
  }, []);

  const toggleNotifications = () => {
    const next = !notificationsOpen;
    setNotificationsOpen(next);
    if (next) loadNotifications();
  };

  const openNotification = item => {
    if (!item?.notificationId) return;
    setNotificationsOpen(false);
    history.push(`/notification/edit/${item.notificationId}`, item);
  };

  const goToAllNotifications = () => {
    setNotificationsOpen(false);
    history.push("/notification");
  };

  useEffect(() => {
    setActiveIndex(0);
  }, [combinedResults]);

  useEffect(() => {
    // Clear when navigating so stale results don't stick
    setQuery("");
    setOpen(false);
    setEntityResults({ stores: [], devices: [], receipts: [] });
  }, [history.location.pathname]);

  const runEntitySearch = async q => {
    const seq = ++requestSeq.current;
    setLoading(true);
    const listParams = {
      page: { limit: 5, pageId: 1 },
      sortBy: { direction: "DESC", property: "createdAt" },
      search: q
    };

    const storeReq = !isStore
      ? Api.post("/admin/store/list", listParams)
          .then(res => res.data?.data?.list || [])
          .catch(() => [])
      : Promise.resolve([]);

    const deviceUrl = isStore
      ? `/admin/device/list?storeId=${storeId}`
      : "/admin/device/list";
    const deviceReq = Api.post(deviceUrl, {
      ...listParams,
      deviceFilterType: "ALL"
    })
      .then(res => res.data?.data?.list || [])
      .catch(() => []);

    const receiptUrl = isStore
      ? `/admin/receiptlist?storeId=${storeId}`
      : "/admin/receiptlist";
    const receiptReq = Api.post(receiptUrl, {
      ...listParams,
      receiptTypeFilter: "ALL"
    })
      .then(res => res.data?.data?.list || [])
      .catch(() => []);

    const [stores, devices, receipts] = await Promise.all([
      storeReq,
      deviceReq,
      receiptReq
    ]);

    if (seq !== requestSeq.current) return;
    setEntityResults({
      stores: stores.slice(0, 5),
      devices: devices.slice(0, 5),
      receipts: receipts.slice(0, 5)
    });
    setLoading(false);
  };

  const onQueryChange = value => {
    setQuery(value);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = value.trim();
    if (!trimmed) {
      setEntityResults({ stores: [], devices: [], receipts: [] });
      setLoading(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      runEntitySearch(trimmed);
    }, SEARCH_DEBOUNCE_MS);
  };

  const closeSearch = () => {
    setOpen(false);
  };

  const navigateToResult = item => {
    if (!item) return;
    closeSearch();
    setQuery("");
    setEntityResults({ stores: [], devices: [], receipts: [] });

    if (item.type === "menu") {
      history.push(item.path);
      return;
    }

    if (item.type === "store" && item.data) {
      history.push(`/store/edit/${item.data.storeId}`, item.data);
      return;
    }

    if (item.type === "device") {
      history.push(isStore ? "/store/device" : "/device");
      return;
    }

    if (item.type === "receipt") {
      history.push(isStore ? "/store/receipt" : "/receipt");
    }
  };

  const onSearchKeyDown = e => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
    }

    if (e.key === "Escape") {
      closeSearch();
      if (searchInputRef.current) searchInputRef.current.blur();
      return;
    }

    if (!combinedResults.length) {
      if (e.key === "Enter") e.preventDefault();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(i => (i + 1) % combinedResults.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(
        i => (i - 1 + combinedResults.length) % combinedResults.length
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      navigateToResult(combinedResults[activeIndex] || combinedResults[0]);
    }
  };

  const userSignout = () => {
    Api.get("/admin/logout")
      .then(res => {
        if (toaster.current) toaster.current.success(res.data.message);
        setTimeout(() => {
          props.logoutAndClearSession();
        }, 1000);
      })
      .catch(error => {
        // Still clear local session if the logout API fails
        props.logoutAndClearSession();
        if (toaster.current) toaster.current.error(error);
      });
  };

  const showDropdown = open && query.trim().length > 0;
  const hasResults = combinedResults.length > 0;

  return (
    <HeaderWrapper {...props}>
      <div className="headerBack">
        <div className="drawer-handle-arrow">
          <button
            type="button"
            className="top-header-icon"
            onClick={() => drawerMiniMethod()}
          >
            <i className="fas fa-bars"></i>
          </button>
        </div>
        <div
          className="mini-drawer-menu-icon"
          onClick={() => drawerMiniMethod()}
        >
          <i className="fas fa-bars" />
          <span className="app-name">Green Till</span>
        </div>

        <div className="header-search-wrap" ref={searchWrapRef}>
          <i className="fas fa-search header-search-icon" />
          <input
            ref={searchInputRef}
            type="search"
            className="header-search-input"
            placeholder="Search menu, stores, devices, invoices…"
            value={query}
            autoComplete="off"
            onChange={e => onQueryChange(e.target.value)}
            onFocus={() => {
              if (query.trim()) setOpen(true);
            }}
            onKeyDown={onSearchKeyDown}
            aria-label="Search CMS"
            aria-autocomplete="list"
            aria-controls="header-search-results"
          />
          {query && (
            <button
              type="button"
              className="header-search-clear"
              aria-label="Clear search"
              onClick={() => {
                onQueryChange("");
                if (searchInputRef.current) searchInputRef.current.focus();
              }}
            >
              <i className="fas fa-times" />
            </button>
          )}

          {showDropdown && (
            <div
              id="header-search-results"
              className="header-search-dropdown"
              role="listbox"
            >
              {loading && !hasResults && (
                <div className="header-search-empty">Searching…</div>
              )}
              {!loading && !hasResults && (
                <div className="header-search-empty">
                  No matches for “{query.trim()}”
                </div>
              )}
              {hasResults &&
                combinedResults.map((item, index) => (
                  <button
                    type="button"
                    key={item.id}
                    role="option"
                    aria-selected={index === activeIndex}
                    className={
                      index === activeIndex
                        ? "header-search-item header-search-item--active"
                        : "header-search-item"
                    }
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => navigateToResult(item)}
                  >
                    <span className="header-search-item__icon">
                      <i className={item.icon} />
                    </span>
                    <span className="header-search-item__text">
                      <span className="header-search-item__label">
                        {item.label}
                      </span>
                      <span className="header-search-item__sub">
                        {item.subtitle}
                      </span>
                    </span>
                    <span className="header-search-item__type">
                      {item.type === "menu" ? "Menu" : item.group}
                    </span>
                  </button>
                ))}
            </div>
          )}
        </div>

        <div className="header-right">
          <div className="header-bell-wrap" ref={bellWrapRef}>
            <button
              type="button"
              className="top-header-icon header-bell-btn"
              aria-label="Notifications"
              aria-expanded={notificationsOpen}
              aria-controls="header-notifications-panel"
              onClick={toggleNotifications}
            >
              <i className="far fa-bell" style={{ fontSize: 16 }}></i>
              {notifications.length > 0 && (
                <span className="header-bell-badge">
                  {notifications.length > 9 ? "9+" : notifications.length}
                </span>
              )}
            </button>

            {notificationsOpen && (
              <div
                id="header-notifications-panel"
                className="header-notifications-dropdown"
                role="menu"
              >
                <div className="header-notifications-dropdown__header">
                  <span>Notifications</span>
                  <button
                    type="button"
                    className="header-notifications-dropdown__refresh"
                    onClick={loadNotifications}
                    disabled={notificationsLoading}
                  >
                    {notificationsLoading ? "…" : "Refresh"}
                  </button>
                </div>

                {notificationsLoading && notifications.length === 0 ? (
                  <div className="header-notifications-dropdown__empty">
                    Loading…
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="header-notifications-dropdown__empty">
                    No notification campaigns yet. Create one under Mobile App →
                    Notifications.
                  </div>
                ) : (
                  <ul className="header-notifications-dropdown__list">
                    {notifications.map(item => (
                      <li key={item.notificationId}>
                        <button
                          type="button"
                          className="header-notifications-item"
                          onClick={() => openNotification(item)}
                        >
                          <span className="header-notifications-item__title">
                            {item.title || "Untitled"}
                          </span>
                          {item.description && (
                            <span className="header-notifications-item__desc">
                              {item.description}
                            </span>
                          )}
                          {item.createdAt && (
                            <span className="header-notifications-item__time">
                              {moment
                                .utc(item.createdAt)
                                .local()
                                .fromNow()}
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="header-notifications-dropdown__footer">
                  <button
                    type="button"
                    className="header-notifications-dropdown__all"
                    onClick={goToAllNotifications}
                  >
                    View all notifications
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="header-user-info">
            <span className="user-name">{displayName}</span>
            <span className="user-role">{displayRole}</span>
          </div>

          <div id="profile">
            <img
              className="top-header-profile-class"
              src={ProfileLockScreen}
              alt="profile"
            />
          </div>
          <UncontrolledPopover
            className="roy-menu"
            innerClassName="roy-inner-content"
            placement="bottom-end"
            target="profile"
            trigger="legacy"
          >
            <PopoverBody>
              <div
                className="roy-menu-list"
                onClick={() => props.history.push("/profile")}
              >
                My Profile
              </div>
              <div className="roy-menu-list" onClick={userSignout}>
                Logout
              </div>
            </PopoverBody>
          </UncontrolledPopover>
        </div>
      </div>
      <Toaster ref={toaster} />
    </HeaderWrapper>
  );
};

const mapStateToProps = state => ({
  authData: {
    adminType: state.auth.adminType,
    storeName: state.auth.storeName
  },
  isStore: state.storeID?.isStore,
  storeId: state.storeID?.storeId
});

export default compose(
  withRouter,
  injectIntl,
  connect(mapStateToProps, { logoutAndClearSession })
)(Header);
